<ng-container
  [ngTemplateOutlet]="navigation"
  [ngTemplateOutletContext]="{
    mobile: false,
    expanded: menuService.showSideBar(),
  }"
/>
<dialog
  #mobileDialog
  id="mobile-navigation-dialog"
  class="mobile-navigation-dialog lg:hidden"
  aria-label="Navigation menu"
  [appModalOpen]="menuService.showMobileMenu()"
  (modalClosed)="closeMobileMenu()"
>
  @if (menuService.showMobileMenu()) {
    <ng-container
      [ngTemplateOutlet]="navigation"
      [ngTemplateOutletContext]="{ mobile: true, expanded: true }"
    />
  }
</dialog>

<ng-template #navigation let-mobile="mobile" let-expanded="expanded">
  <!-- Bloomberg Terminal Pro Sidebar -->
  <nav
    [id]="mobile ? 'mobile-navigation' : 'main-navigation'"
    tabindex="-1"
    role="navigation"
    aria-label="Main navigation"
    [class]="
      'bg-bb-bg-secondary h-full flex-col justify-between transition-all duration-200 border-r border-bb-border ' +
      (mobile
        ? 'flex w-full'
        : expanded
          ? 'hidden w-60 lg:flex'
          : 'hidden w-14 lg:flex')
    "
  >
    <!-- Logo Section -->
    <div
      [class]="
        'h-14 flex items-center border-b border-bb-border ' +
        (expanded ? 'px-3 justify-between' : 'px-0 justify-center')
      "
    >
      @if (expanded) {
        <a routerLink="/dashboard" class="flex items-center gap-2.5 group">
          <div
            class="w-8 h-8 bg-bb-accent-blue rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <span class="font-mono font-bold text-sm text-bb-bg-primary"
              >R</span
            >
          </div>
          <span
            class="font-display font-semibold text-bb-text-primary tracking-tight text-base"
            >RAZOR</span
          >
        </a>
      } @else {
        <a
          routerLink="/dashboard"
          class="w-8 h-8 bg-bb-accent-blue rounded-lg flex items-center justify-center"
        >
          <span class="font-mono font-bold text-sm text-bb-bg-primary">R</span>
        </a>
      }
      @if (mobile) {
        <button
          type="button"
          class="p-2 text-bb-text-secondary"
          (click)="closeMobileMenu()"
          aria-label="Close navigation menu"
          autofocus
        >
          <ui-icon name="times" size="sm" aria-hidden="true" />
        </button>
      } @else if (expanded) {
        <!-- RF-QA-185: the ONE "Collapse sidebar" control (the footer used to
             render an identical twin). Rendered only while expanded; the
             collapsed state exposes a single "Expand sidebar" button in the
             footer instead. -->
        <button
          type="button"
          (click)="toggleSidebar()"
          class="text-bb-text-muted hover:text-bb-text-secondary p-1.5 rounded transition-colors duration-150"
          aria-label="Collapse sidebar"
        >
          <ui-icon name="angle-double-left" size="sm" aria-hidden="true" />
        </button>
      }
    </div>

    @if (mobile) {
      <button
        type="button"
        class="m-3 flex items-center gap-2 rounded border border-bb-border px-3 py-2 text-bb-text-primary"
        (click)="openPalette(true)"
      >
        <ui-icon name="search" size="sm" aria-hidden="true" />
        <span>Search commands and routes</span>
      </button>
    }

    <!-- GAP-FU-01 · Recent rail: shows last 5 palette entries when sidebar is expanded. -->
    @if (expanded && recentRows().length > 0) {
      <div class="border-b border-bb-border px-3 py-2">
        <div class="flex items-center justify-between mb-1.5">
          <span
            class="font-mono text-2xs font-medium text-bb-text-muted uppercase tracking-wider"
            >Recent</span
          >
          <button
            type="button"
            class="font-mono text-3xs text-bb-text-muted hover:text-bb-text-secondary transition-colors"
            (click)="openPalette(mobile)"
            [attr.aria-label]="'Open command palette'"
          >
            {{ modKey }}K
          </button>
        </div>
        <!-- RF-QA-189/190: plain list of buttons — no menu/menuitem roles
             (the menu pattern promises arrow-key navigation we do not implement). -->
        <ul class="space-y-0.5" aria-label="Recent items">
          @for (row of recentRows(); track row.id) {
            <li>
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-body-xs text-bb-text-secondary hover:bg-bb-bg-tertiary hover:text-bb-text-primary transition-colors"
                [title]="row.label"
                (click)="invokeRecent(row, mobile)"
              >
                <ui-icon
                  name="history"
                  size="xs"
                  class="text-bb-text-muted"
                  aria-hidden="true"
                ></ui-icon>
                <span class="truncate">{{ row.label }}</span>
              </button>
            </li>
          }
        </ul>
      </div>
    }

    <!-- Menu Sections -->
    <div
      class="sidebar-scroll flex-1 overflow-y-auto overflow-x-hidden pt-3 pb-8 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-bb-border"
    >
      @for (menuGroup of menu; track menuGroup.group) {
        <!--
        DEV-201 / GAP-008: Tenant entitlement gating. Groups carrying a
        `feature` (e.g. Trading, Risk Management) render through the
        *appHasFeature structural directive, which hides them — and re-shows
        them reactively on tenant switch — when the active tenant lacks the
        entitlement. Groups without a `feature` always render. Both branches
        reuse #groupTpl so there is a single source of truth for the group body.
      -->
        @if (menuGroup.feature) {
          <ng-container
            *appHasFeature="menuGroup.feature"
            [ngTemplateOutlet]="groupTpl"
            [ngTemplateOutletContext]="{ $implicit: menuGroup }"
          />
        } @else {
          <ng-container
            [ngTemplateOutlet]="groupTpl"
            [ngTemplateOutletContext]="{ $implicit: menuGroup }"
          />
        }
      }
    </div>

    <ng-template #groupTpl let-menuGroup>
      <div class="mb-1">
        <!-- Section Header -->
        @if (expanded) {
          <div class="px-3 pt-3 pb-1.5">
            <span
              class="font-mono text-2xs font-medium text-bb-text-muted uppercase tracking-wider"
            >
              {{ menuGroup.group }}
            </span>
          </div>
        }

        <!-- Menu Items.
             RF-QA-189/190: a plain <ul> of links inside the single
             <nav aria-label="Main navigation"> — the former role=menubar /
             role=menuitem pattern advertised arrow-key behaviour the sidebar
             never implemented. The active link is marked with aria-current=page
             via routerLinkActive's ariaCurrentWhenActive. -->
        <ul class="space-y-0.5 px-1.5" [attr.aria-label]="menuGroup.group">
          @for (item of menuGroup.items; track trackByMenuItem($index, item)) {
            <li>
              <!-- Menu Item -->
              <div class="group relative">
                <!-- Parent Menu (no children) -->
                @if (!item.children || item.children.length === 0) {
                  <a
                    [routerLink]="item.route"
                    routerLinkActive="bg-bb-highlight/20 text-bb-accent-blue"
                    ariaCurrentWhenActive="page"
                    #rla="routerLinkActive"
                    [uiTooltip]="!expanded ? item.label : ''"
                    tooltipPosition="right"
                    [tooltipDelay]="150"
                    class="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-bb-text-secondary hover:text-bb-text-primary hover:bg-bb-bg-tertiary transition-colors duration-150"
                    [class.px-0]="!expanded"
                    [class.justify-center]="!expanded"
                  >
                    <!-- Active Indicator -->
                    @if (rla.isActive) {
                      <div
                        class="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-bb-accent-blue rounded-r-sm"
                      ></div>
                    }

                    <!-- Icon -->
                    <svg-icon
                      [src]="item.icon || ''"
                      [svgClass]="'w-4 h-4 flex-shrink-0'"
                    ></svg-icon>

                    <!-- Label & Badge -->
                    @if (expanded) {
                      <span class="text-body-sm font-medium truncate flex-1">{{
                        item.label
                      }}</span>
                      @if (item.badge) {
                        <ui-badge
                          [label]="item.badge"
                          [variant]="getBadgeVariant(item.badgeColor)"
                          size="sm"
                          class="ml-auto"
                        >
                        </ui-badge>
                      }
                    }
                  </a>
                } @else {
                  <!-- Child Menu (has children) -->
                  <!--
                    GAP-FE-UX12 r2: aria-label only when sidebar is
                    collapsed (icon-only). When expanded, the inner
                    <span>{{ item.label }}</span> below provides the
                    accessible name — duplicating it via aria-label
                    risks WCAG 2.5.3 (Label in Name) divergence.
                    The svg-icon is purely decorative.
                  -->
                  <button
                    type="button"
                    (click)="menuService.toggleMenu(item)"
                    [attr.aria-label]="expanded ? null : item.label"
                    [attr.aria-expanded]="item.expanded"
                    [uiTooltip]="!expanded ? item.label : ''"
                    tooltipPosition="right"
                    [tooltipDelay]="150"
                    class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-bb-text-secondary hover:text-bb-text-primary hover:bg-bb-bg-tertiary transition-colors duration-150"
                    [class.px-0]="!expanded"
                    [class.justify-center]="!expanded"
                  >
                    <!-- Icon (decorative) -->
                    <svg-icon
                      [src]="item.icon || ''"
                      [svgClass]="'w-4 h-4 flex-shrink-0'"
                      aria-hidden="true"
                    ></svg-icon>

                    <!-- Label & Chevron -->
                    @if (expanded) {
                      <span
                        class="text-body-sm font-medium truncate flex-1 text-left"
                        >{{ item.label }}</span
                      >
                      @if (item.badge) {
                        <ui-badge
                          [label]="item.badge"
                          [variant]="getBadgeVariant(item.badgeColor)"
                          size="sm"
                        >
                        </ui-badge>
                      }
                      <ui-icon
                        name="chevron-right"
                        size="sm"
                        class="text-bb-text-muted transition-transform duration-200"
                        [class.rotate-90]="item.expanded"
                        aria-hidden="true"
                      />
                    }
                  </button>
                }
              </div>

              <!-- Submenu Items -->
              @if (
                item.expanded &&
                item.children &&
                item.children.length > 0 &&
                expanded
              ) {
                <ul
                  class="ml-5 mt-1 space-y-0.5 border-l border-bb-border pl-2.5"
                >
                  @for (
                    child of item.children;
                    track trackByChildItem($index, child)
                  ) {
                    <li>
                      <!-- exact: a parent-path child ("All Reports" → /reports)
                           must not ALSO be current on /reports/daily-desk. -->
                      <a
                        [routerLink]="child.route"
                        routerLinkActive="text-bb-accent-blue bg-bb-highlight/10"
                        [routerLinkActiveOptions]="{ exact: true }"
                        ariaCurrentWhenActive="page"
                        class="flex items-center h-8 px-2 rounded text-body-xs font-medium text-bb-text-secondary hover:text-bb-text-primary hover:bg-bb-bg-tertiary transition-colors duration-150"
                      >
                        {{ child.label }}
                      </a>
                    </li>
                  }
                </ul>
              }
            </li>
          }
        </ul>

        <!-- Separator -->
        @if (menuGroup.separator) {
          <!-- RF-QA-156: the divider host is `w-full`; with mx-4 that is wider
               than the scroller and produced a horizontal scrollbar. -->
          <ui-divider styleClass="my-3 mx-4 !w-auto"></ui-divider>
        }
      </div>
    </ng-template>

    <!-- User Profile Section -->
    <div class="border-t border-bb-border">
      @if (expanded) {
        <div class="px-3 py-2.5 flex items-center gap-2.5">
          <ui-avatar
            [name]="currentUserInitial()"
            [ariaLabel]="currentUserName()"
            size="sm"
            shape="circle"
            [status]="connectionStatus()"
          >
          </ui-avatar>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-bb-text-primary truncate">
              {{ currentUserName() }}
            </p>
            <p class="text-xs text-bb-text-muted truncate">
              {{ currentUserRole() }}
            </p>
          </div>
        </div>
      } @else {
        <div class="py-2.5 flex justify-center">
          <ui-avatar
            [name]="currentUserInitial()"
            [ariaLabel]="currentUserName()"
            [uiTooltip]="currentUserName()"
            tooltipPosition="right"
            size="sm"
            shape="circle"
            [status]="connectionStatus()"
          >
          </ui-avatar>
        </div>
      }
    </div>

    <!-- Footer - Version -->
    <div
      [class]="
        'h-10 border-t border-bb-border flex items-center ' +
        (expanded ? 'px-3 justify-between' : 'justify-center')
      "
    >
      @if (expanded) {
        <span class="font-mono text-2xs text-bb-text-muted"
          >v{{ appVersion }}</span
        >
        <!-- RF-QA-185: the footer no longer duplicates the logo-bar
             "Collapse sidebar" button; only the collapsed-state "Expand
             sidebar" control lives here. -->
      } @else {
        <button
          type="button"
          (click)="toggleSidebar()"
          class="text-bb-text-muted hover:text-bb-text-secondary p-1.5 rounded transition-colors duration-150"
          aria-label="Expand sidebar"
        >
          <ui-icon
            name="angle-double-left"
            size="sm"
            class="rotate-180"
            aria-hidden="true"
          />
        </button>
      }
    </div>
  </nav>
</ng-template>
